import React, { Component } from 'react';
import { Tooltip, Button, Input, message, Select, Tag, Modal, Form, Checkbox, DatePicker } from 'antd';
import { fetchData } from '../lib/apis';
import config from '../lib/config';
import { AccountMetaData } from '../config/settings';
import moment from 'moment';

import 'moment/locale/vi';

moment.locale('vi');

const { Option } = Select;

const stageMap = {
    GREETING: { name: 'Chào hỏi', color: '#8c8c8c' },
    DISCOVERY: { name: 'Tìm hiểu', color: '#0071e3' },
    PRESENTATION: { name: 'Tư vấn', color: '#2997ff' },
    NEGOTIATION: { name: 'Thương lượng', color: '#ff9f0a' },
    CLOSING: { name: 'Chốt đơn', color: '#34c759' },
    HANDOVER: { name: 'Bàn giao', color: '#bf5af2' },
    FOLLOW_UP: { name: 'Chăm sóc', color: '#64d2ff' }
};

const orderStatusMap = {
    draft: { label: 'Nháp', color: '#8c8c8c', icon: 'far fa-file-alt' },
    pending_confirmation: { label: 'Chờ xác nhận', color: '#ff9f0a', icon: 'far fa-clock' },
    confirmed: { label: 'Đã xác nhận', color: '#34c759', icon: 'far fa-check-circle' },
    awaiting_shipment: { label: 'Chờ giao vận', color: '#0071e3', icon: 'fas fa-box-open' },
    shipping: { label: 'Đang giao', color: '#2997ff', icon: 'fas fa-shipping-fast' },
    delivered: { label: 'Đã giao', color: '#248a3d', icon: 'fas fa-hand-holding-heart' },
    failed_delivery: { label: 'Giao thất bại', color: '#ff453a', icon: 'fas fa-exclamation-triangle' },
    returned: { label: 'Hoàn hàng', color: '#ff9f0a', icon: 'fas fa-undo' },
    cancelled: { label: 'Đã hủy', color: '#6e6e73', icon: 'fas fa-times-circle' }
};

const getStructuredCallSummaryTags = (structuredData = {}) => {
    const data = structuredData && typeof structuredData === 'object' ? structuredData : {};
    const customer = data.customer && typeof data.customer === 'object' ? data.customer : {};
    const tags = [];
    const pushTag = (label, value, tone = 'neutral') => {
        if (!value) return;
        const text = String(value).trim();
        if (!text) return;
        tags.push({ label, value: text, tone });
    };

    (Array.isArray(data.interests) ? data.interests : []).forEach((item) => pushTag('Quan tâm', item, 'accent'));
    pushTag('Ngân sách', data.budget, 'warning');
    (Array.isArray(data.objections) ? data.objections : []).forEach((item) => pushTag('Phản đối', item, 'danger'));
    pushTag('Thái độ', data.sentiment, 'neutral');
    pushTag('Họ tên', customer.name, 'neutral');
    pushTag('SĐT', customer.phone, 'accent');
    pushTag('Email', customer.email, 'accent');
    pushTag('Địa chỉ', customer.address, 'neutral');
    pushTag('Ngày hẹn', data.appointment_date, 'success');
    pushTag('Công ty', data.branch_location, 'neutral');

    return {
        tags,
        notes: data.notes ? String(data.notes).trim() : ''
    };
};

const joinClassNames = (...values) => values.filter(Boolean).join(' ');

const getContactDisplayName = (contact = {}) => {
    const from = contact.from || {};
    return from.full_name_other || contact.thread_title || contact.full_name || 'Khách hàng chưa định danh';
};

const getSourceLabel = (contact = {}) => {
    if (contact.type == 'callcenter') return 'Callcenter';
    if (contact.type == 'www' || contact.type == 'webchat') return 'Website';
    if (contact.channel && config.type_account_social[contact.channel.acc_type]) {
        return config.type_account_social[contact.channel.acc_type].name || contact.channel.full_name;
    }
    return contact.channel ? contact.channel.full_name : 'Kênh chưa xác định';
};

const getHeroMeta = (contact = {}) => {
    const from = contact.from || {};
    return from.phone || from.phone2 || from.email || 'Đang theo dõi hội thoại realtime';
};

const getSourceIcon = (contact = {}) => {
    if (!contact.channel || !config.type_account_social[contact.channel.acc_type]) return null;
    const accountConfig = config.type_account_social[contact.channel.acc_type];
    if (accountConfig.icon) {
        const iconSrc = typeof accountConfig.icon === 'object'
            ? accountConfig.icon.default || accountConfig.icon
            : accountConfig.icon;

        return <img src={iconSrc} alt={accountConfig.name || 'icon'} />;
    }

    return <span>{accountConfig.name}</span>;
};

const buildTagGroups = (settingSystem) => {
    if (!settingSystem || !Array.isArray(settingSystem.tags)) return [];

    return settingSystem.tags
        .filter((item) => !item.parent_id)
        .map((item) => ({
            ...item,
            tag_childs: settingSystem.tags.filter((tag) => tag.parent_id == item.tag_id)
        }));
};

const getSelectedTagId = (tagGroups) => {
    const selected = tagGroups.find((item) => item.is_select);
    return selected ? selected.tag_id : (tagGroups[0] ? tagGroups[0].tag_id : undefined);
};

const getExtractedSignals = (aiInfo = {}) => {
    const extracted = aiInfo.extracted_info || {};
    const signals = [];

    if (extracted.phone) signals.push({ label: 'SĐT', value: extracted.phone, tone: 'accent' });
    if (extracted.email) signals.push({ label: 'Email', value: extracted.email, tone: 'accent' });
    if (extracted.product) signals.push({ label: 'Sản phẩm', value: extracted.product, tone: 'neutral' });
    if (extracted.address) signals.push({ label: 'Địa chỉ', value: extracted.address, tone: 'neutral' });
    if (extracted.appointment_date) signals.push({ label: 'Ngày hẹn', value: extracted.appointment_date, tone: 'success' });

    return signals;
};

const formatUpdatedTime = (value) => {
    if (!value) return 'Chưa có';
    return moment(value).fromNow();
};

const getLeadScoreTone = (score) => {
    const numericScore = Number(score || 0);
    if (numericScore >= 70) return 'success';
    if (numericScore >= 40) return 'warning';
    return 'danger';
};

const getLeadScoreDescription = (score) => {
    const numericScore = Number(score || 0);
    if (numericScore >= 70) return 'Mức ưu tiên cao, có thể chốt sớm.';
    if (numericScore >= 40) return 'Mức ưu tiên trung bình, cần theo dõi thêm.';
    return 'Mức ưu tiên thấp, cần nuôi dưỡng thêm.';
};

const InfoSection = ({ kicker, title, actions, tone = 'light', className, children, style }) => (
    <section className={joinClassNames('info-panel-section', `info-panel-section--${tone}`, className)}
        style={style ? style: undefined}
    >
        {(kicker || title || actions) ? (
            <div className="info-panel-section__header">
                <div className="info-panel-section__copy">
                    {kicker ? <span className="info-panel-section__kicker">{kicker}</span> : null}
                    {title ? <h4 className="info-panel-section__title" style={{fontSize: 15}}>{title}</h4> : null}
                </div>
                {actions ? <div className="info-panel-section__actions">{actions}</div> : null}
            </div>
        ) : null}
        <div className="info-panel-section__body">{children}</div>
    </section>
);

const InfoEmpty = ({ children }) => (
    <div className="info-panel-empty">{children}</div>
);

const InfoBadge = ({ children, tone = 'neutral', className, style }) => (
    <span className={joinClassNames('info-panel-badge', `info-panel-badge--${tone}`, className)} style={style}>
        {children}
    </span>
);

const InfoStat = ({ label, value, accent }) => (
    <div className="info-panel-stat">
        <span className="info-panel-stat__label">{label}</span>
        <strong className="info-panel-stat__value" style={accent ? { color: accent } : undefined}>{value}</strong>
    </div>
);

const orderPreviewFallback = require('../img/no_images.png');

const InfoMetaList = ({ items = [] }) => (
    <div className="info-panel-meta-list">
        {items.map((item) => (
            <div className="info-panel-meta-list__item" key={item.label}>
                <span className="info-panel-meta-list__label" style={{fontSize: 11}}>{item.label}</span>
                <span className="info-panel-meta-list__value" style={{fontSize: 13}}>{item.value}</span>
            </div>
        ))}
    </div>
);

const InfoHero = ({ contact, aiInfo }) => {
    const displayName = getContactDisplayName(contact);
    const sourceLabel = getSourceLabel(contact);
    const heroMeta = getHeroMeta(contact);
    const extractedSignals = getExtractedSignals(aiInfo || {});
    const aiNote = aiInfo && aiInfo.extracted_info ? aiInfo.extracted_info.notes : '';

    return (
        <section className="info-panel-hero">
            {aiInfo ? (
                <div className="info-panel-hero__status">
                    <InfoBadge tone="accent">AI đang hoạt động</InfoBadge>
                </div>
            ) : null}
            <div className="info-panel-hero__identity">
                <div className="info-panel-avatar">
                    <img
                        src={contact.channel ? contact.channel.avatar : 'undefined'}
                        className="info-panel-avatar__image"
                        onError={(event) => {
                            event.target.src = '/user.jpg';
                        }}
                        alt={displayName}
                    />
                    <div className="info-panel-avatar__badge">
                        {getSourceIcon(contact)}
                    </div>
                </div>
                <div className="info-panel-hero__copy">
                    <span className="info-panel-hero__eyebrow">{sourceLabel}</span>
                    <h2 className="info-panel-hero__title">{displayName}</h2>
                    <p className="info-panel-hero__subtitle">{heroMeta}</p>
                </div>
            </div>

            {extractedSignals.length > 0 ? (
                <div className="info-panel-hero__signals">
                    {extractedSignals.map((signal) => (
                        <InfoBadge key={`${signal.label}-${signal.value}`} tone={signal.tone}>{signal.label}: {signal.value}</InfoBadge>
                    ))}
                </div>
            ) : null}

            {aiNote ? (
                <div className="info-panel-hero__note">
                    <span className="info-panel-hero__note-kicker">Ghi chú AI</span>
                    <p>{aiNote}</p>
                </div>
            ) : null}
        </section>
    );
};

export default class InfoContact extends Component {
    constructor(props) {
        super(props);
        this.state = {
            time_support: true,
            memoryData: null,
            loadingMemory: false,
            callSummaries: [],
            loadingCallSummaries: false,
            customerOrders: [],
            loadingCustomerOrders: false,
            expandedOrders: {},
            confirmingOrderId: ''
        };
    }

    componentDidMount() {
        if (this.props.contactInfoSelected) {
            this.fetchMemory();
            this.fetchCallSummaries();
            this.fetchCustomerOrders();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.contactInfoSelected && (!prevProps.contactInfoSelected || prevProps.contactInfoSelected.contact_id !== this.props.contactInfoSelected.contact_id)) {
            this.fetchMemory();
            this.fetchCallSummaries();
            this.setState({ expandedOrders: {} });
            this.fetchCustomerOrders();
        } else if (this.props.orderListRefreshToken !== prevProps.orderListRefreshToken) {
            this.fetchCustomerOrders();
        }
    }

    fetchMemory = async () => {
        const { contactInfoSelected } = this.props;
        if (!contactInfoSelected || !contactInfoSelected.contact_id) return;

        const companyId = contactInfoSelected.company_id || AccountMetaData.getCompanyId();
        this.setState({ loadingMemory: true });

        try {
            const res = await fetchData({
                url: 'retrieval/memory',
                params: {
                    companyId,
                    contactId: contactInfoSelected.contact_id
                }
            });

            this.setState({
                memoryData: res.code === 200 ? res.data : null,
                loadingMemory: false
            });
        } catch (err) {
            console.error('Fetch memory error:', err);
            this.setState({ loadingMemory: false, memoryData: null });
        }
    }

    fetchCallSummaries = async () => {
        const { contactInfoSelected } = this.props;
        if (!contactInfoSelected || !contactInfoSelected.contact_id) return;

        this.setState({ loadingCallSummaries: true });

        try {
            const res = await fetchData({
                url: `api/v1/contact/${contactInfoSelected.contact_id}/call-summaries?limit=10`,
                method: 'get'
            });

            this.setState({
                callSummaries: res && res.code === 200 && Array.isArray(res.data) ? res.data : [],
                loadingCallSummaries: false
            });
        } catch (err) {
            console.error('Fetch call summaries error:', err);
            this.setState({ loadingCallSummaries: false, callSummaries: [] });
        }
    }

    fetchCustomerOrders = async () => {
        const { contactInfoSelected } = this.props;
        if (!contactInfoSelected || !contactInfoSelected.contact_id) return;

        this.setState({ loadingCustomerOrders: true });

        try {
            const res = await fetchData({
                url: 'api/v1/orders-v2/list',
                method: 'get',
                params: {
                    contact_id: contactInfoSelected.contact_id,
                    limit: 20,
                    page: 1
                }
            });

            this.setState({
                customerOrders: Array.isArray(res.data) ? res.data : [],
                loadingCustomerOrders: false
            });
        } catch (err) {
            console.error('Fetch customer orders error:', err);
            this.setState({
                customerOrders: [],
                loadingCustomerOrders: false
            });
        }
    }

    formatCurrency = (value) => value === 0 ? `0đ`: `${new Intl.NumberFormat('vi-VN').format(value || 0)}đ`;

    getOrderStatusMeta = (status) => orderStatusMap[status] || { label: status || 'Không xác định', color: '#6e6e73', icon: 'far fa-question-circle' };

    getOrderPreviewItems = (order) => {
        const items = Array.isArray(order && order.items) ? order.items : [];
        const previewItems = items.slice(0, 3).map((item, index) => {
            const snapshot = item && item.variant_snapshot ? item.variant_snapshot : {};
            return {
                key: `${order && (order._id || order.order_code) || 'order'}_${item && item.variant_id || 'item'}_${index}`,
                image: `${snapshot.image || ''}`.trim(),
                label: snapshot.name || snapshot.sku || `Sản phẩm ${index + 1}`
            };
        });

        return {
            items: previewItems,
            total: items.length
        };
    }

    toggleOrderExpanded = (orderId) => {
        this.setState((prevState) => ({
            expandedOrders: {
                ...prevState.expandedOrders,
                [orderId]: !prevState.expandedOrders[orderId]
            }
        }));
    }

    handleConfirmDraftOrder = async (order) => {
        if (!order || !order._id) return;

        const pricing = order.pricing || {};
        const payload = {
            order_id: order._id,
            customer: order.customer || {},
            items: (Array.isArray(order.items) ? order.items : []).map((item) => ({
                variant_id: item.variant_id,
                quantity: item.quantity,
                image: item.variant_snapshot ? item.variant_snapshot.image || '' : ''
            })),
            notes: order.notes || {},
            discount: pricing.discount || 0,
            shipping_fee: pricing.shipping_fee || 0,
            source: {
                ...(order.source || {}),
                contact_id: order.source && order.source.contact_id
                    ? order.source.contact_id
                    : (this.props.contactInfoSelected && this.props.contactInfoSelected.contact_id) || ''
            }
        };

        this.setState({ confirmingOrderId: order._id });

        try {
            const response = await fetchData({
                url: 'api/v1/orders-v2/confirm',
                method: 'post',
                data: payload
            });

            this.setState({ confirmingOrderId: '' }, () => {
                if (this.props.onOrderConfirmed) {
                    this.props.onOrderConfirmed(response.data || null);
                    return;
                }

                this.fetchCustomerOrders();
            });

            message.success({ content: 'Xác nhận đơn hàng thành công', key: `confirm-order-${order._id}` });
        } catch (err) {
            this.setState({ confirmingOrderId: '' });
            message.error({ content: err.message || 'Xác nhận đơn hàng thất bại', key: `confirm-order-${order._id}` });
        }
    }

    handleForceSummarize = async () => {
        const { contactInfoSelected } = this.props;
        const companyId = contactInfoSelected.company_id || AccountMetaData.getCompanyId();

        message.loading({ content: 'Đang tóm tắt...', key: 'summarize' });

        try {
            const res = await fetchData({
                url: 'retrieval/memory/summarize',
                method: 'post',
                data: {
                    companyId,
                    contactId: contactInfoSelected.contact_id
                }
            });

            if (res.code === 200) {
                message.success({ content: 'Tóm tắt thành công!', key: 'summarize' });
                this.fetchMemory();
            } else {
                message.error({ content: res.message || 'Lỗi tóm tắt', key: 'summarize' });
            }
        } catch (err) {
            message.error({ content: 'Đã có lỗi xảy ra!', key: 'summarize' });
        }
    }

    handleClearMemory = () => {
        const { contactInfoSelected } = this.props;
        const companyId = contactInfoSelected.company_id || AccountMetaData.getCompanyId();

        Modal.confirm({
            title: 'Đặt lại toàn bộ dữ liệu AI?',
            content: 'Việc này sẽ xóa sạch: Lịch sử, Thông tin đã trích xuất, và đưa AI về trạng thái bắt đầu mới hoàn toàn.',
            okText: 'Xóa sạch',
            okType: 'danger',
            cancelText: 'Hủy',
            onOk: async () => {
                try {
                    const res = await fetchData({
                        url: 'retrieval/memory',
                        method: 'delete',
                        params: {
                            companyId,
                            contactId: contactInfoSelected.contact_id
                        }
                    });

                    if (res.code === 200) {
                        message.success('Đã xóa bộ nhớ!');
                        this.fetchMemory();
                    }
                } catch (err) {
                    message.error('Lỗi khi xóa bộ nhớ');
                }
            }
        });
    }

    renderOrderItems = (order) => {
        const items = Array.isArray(order.items) ? order.items : [];
        if (!items.length) {
            return <InfoEmpty>Đơn hàng chưa có sản phẩm.</InfoEmpty>;
        }

        return (
            <div className="info-order-items">
                {items.map((item, index) => {
                    const snapshot = item.variant_snapshot || {};
                    const attributes = snapshot.attributes || {};
                    const attributeKeys = Object.keys(attributes).filter((key) => `${attributes[key] || ''}`.trim());

                    return (
                        <div className="info-order-item" key={`${order._id || order.order_code}_item_${index}`}>
                            <div className="info-order-item__header">
                                <div className="info-order-item__main">
                                    {snapshot.image ? (
                                        <div className="info-order-item__media">
                                            <img
                                                src={snapshot.image}
                                                alt={snapshot.name || snapshot.sku || `Sản phẩm ${index + 1}`}
                                                onError={(event) => {
                                                    event.target.src = orderPreviewFallback;
                                                }}
                                            />
                                        </div>
                                    ) : null}
                                    <div className="info-order-item__content">
                                        <div className="info-order-item__sku">{snapshot.sku || snapshot.name || `Sản phẩm ${index + 1}`}</div>
                                        {snapshot.name ? <div className="info-order-item__name">{snapshot.name}</div> : null}
                                    </div>
                                </div>
                                <div className="info-order-item__total">
                                    <span>SL {item.quantity}</span>
                                    <strong>{this.formatCurrency(item.line_total || 0)}</strong>
                                </div>
                            </div>
                            {attributeKeys.length > 0 ? (
                                <div className="info-order-item__chips">
                                    {attributeKeys.map((key) => (
                                        <InfoBadge key={`${snapshot.sku || snapshot.name}_${key}`} tone="soft">
                                            {key}: {attributes[key]}
                                        </InfoBadge>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        );
    }

    renderAiSection = (aiInfo) => {
        if (!aiInfo) return null;

        const stageMeta = stageMap[aiInfo.current_stage] || { name: aiInfo.current_stage || 'Không xác định', color: '#8c8c8c' };
        const extractedSignals = getExtractedSignals(aiInfo);
        const extractedNotes = aiInfo.extracted_info && aiInfo.extracted_info.notes;
        const scoreTone = getLeadScoreTone(aiInfo.lead_score);

        return (
            <div>
                <div className="info-ai-metrics">
                    <div className="info-ai-metric" style={{ '--metric-accent': stageMeta.color }}>
                        <div className="info-ai-metric__top">
                            <span className="info-ai-metric__label">Giai đoạn hiện tại</span>
                            <InfoBadge tone="soft" className="info-ai-metric__badge">{stageMeta.name}</InfoBadge>
                        </div>
                        <strong className="info-ai-metric__value" style={{ color: stageMeta.color }}>{stageMeta.name}</strong>
                        <p className="info-ai-metric__meta">AI cập nhật theo ngữ cảnh chat mới nhất.</p>
                    </div>
                    <div className={`info-ai-metric info-ai-metric--${scoreTone}`}>
                        <div className="info-ai-metric__top">
                            <span className="info-ai-metric__label">Độ tiềm năng</span>
                            <InfoBadge tone={scoreTone} className="info-ai-metric__badge">{aiInfo.lead_score || 0}%</InfoBadge>
                        </div>
                        <strong className="info-ai-metric__value">{aiInfo.lead_score || 0}%</strong>
                        <p className="info-ai-metric__meta">{getLeadScoreDescription(aiInfo.lead_score)}</p>
                    </div>
                </div>

                {extractedSignals.length > 0 ? (
                    <div className="info-panel-badge-row">
                        {extractedSignals.map((signal) => (
                            <InfoBadge key={`${signal.label}-${signal.value}`} tone={signal.tone}>
                                {signal.label}: {signal.value}
                            </InfoBadge>
                        ))}
                    </div>
                ) : null}

                {extractedNotes ? (
                    <div className="info-panel-quote">
                        <span className="info-panel-quote__label">Ghi chú từ AI</span>
                        <p>{extractedNotes}</p>
                    </div>
                ) : null}
            </div>
        );
    }

    renderMemorySection = () => {
        const { loadingMemory, memoryData } = this.state;
        const semanticMemory = memoryData && memoryData.semanticMemory ? memoryData.semanticMemory : null;
        const memorySignals = [];

        if (semanticMemory) {
            (semanticMemory.interests || []).forEach((item) => memorySignals.push({ label: 'Quan tâm', value: item, tone: 'accent' }));
            if (semanticMemory.budget) memorySignals.push({ label: 'Ngân sách', value: semanticMemory.budget, tone: 'warning' });
            (semanticMemory.objections || []).forEach((item) => memorySignals.push({ label: 'Phản đối', value: item, tone: 'danger' }));
            if (semanticMemory.sentiment) memorySignals.push({ label: 'Thái độ', value: semanticMemory.sentiment, tone: 'neutral' });
            if (semanticMemory.customer && semanticMemory.customer.phone) memorySignals.push({ label: 'SĐT', value: semanticMemory.customer.phone, tone: 'accent' });
            if (!semanticMemory.customer && semanticMemory.phone) memorySignals.push({ label: 'SĐT', value: semanticMemory.phone, tone: 'accent' });
            if (semanticMemory.customer && semanticMemory.customer.email) memorySignals.push({ label: 'Email', value: semanticMemory.customer.email, tone: 'accent' });
            if (!semanticMemory.customer && semanticMemory.email) memorySignals.push({ label: 'Email', value: semanticMemory.email, tone: 'accent' });
            if (semanticMemory.customer && semanticMemory.customer.address) memorySignals.push({ label: 'Địa chỉ', value: semanticMemory.customer.address, tone: 'neutral' });
            if (semanticMemory.appointment_date) memorySignals.push({ label: 'Ngày hẹn', value: semanticMemory.appointment_date, tone: 'success' });
            if (semanticMemory.branch_location) memorySignals.push({ label: 'Công ty', value: semanticMemory.branch_location, tone: 'neutral' });
        }

        return (
            <InfoSection title="Bộ nhớ trợ lý" tone="light">
                {loadingMemory ? (
                    <InfoEmpty>Đang tải dữ liệu bộ nhớ...</InfoEmpty>
                ) : memoryData ? (
                    <div className="info-panel-stack-sm">
                        {memoryData.memorySummary ? (
                            <div className="info-panel-quote" style={{padding: 0}}>
                                <span className="info-panel-quote__label">Tóm tắt hội thoại</span>
                                <p style={{fontSize: 12}}>{memoryData.memorySummary}</p>
                            </div>
                        ) : (
                            <InfoEmpty>Chưa có bản tóm tắt. Có thể tạo tóm tắt đầu tiên ngay trong panel này.</InfoEmpty>
                        )}

                        {memorySignals.length > 0 ? (
                            <div className="info-panel-badge-row">
                                {memorySignals.map((signal) => (
                                    <InfoBadge key={`${signal.label}-${signal.value}`} tone={signal.tone}>
                                        {signal.label}: {signal.value}
                                    </InfoBadge>
                                ))}
                            </div>
                        ) : null}

                        <div className="info-panel-footnote">Cập nhật {formatUpdatedTime(memoryData.memoryUpdatedAt)}</div>
                    </div>
                ) : (
                    <InfoEmpty>Chưa có dữ liệu bộ nhớ.</InfoEmpty>
                )}
            </InfoSection>
        );
    }

    renderCallSummariesSection = () => {
        const { loadingCallSummaries, callSummaries } = this.state;

        return (
            <InfoSection title="Lịch sử tóm tắt cuộc gọi">
                {loadingCallSummaries ? (
                    <InfoEmpty>Đang tải lịch sử tóm tắt cuộc gọi...</InfoEmpty>
                ) : callSummaries.length > 0 ? (
                    <div className="info-summary-list">
                        {callSummaries.map((item) => {
                            const structuredInfo = getStructuredCallSummaryTags(item.structured_data);

                            return (
                                <article className="info-summary-card" key={item._id || item.created_time}>
                                    <div className="info-summary-card__header">
                                        <strong>{item.agent_name || 'Call center'}</strong>
                                        <span>{moment(item.called_at || item.created_time).format('HH:mm DD/MM/YYYY')}</span>
                                    </div>

                                    {structuredInfo.tags.length > 0 ? (
                                        <div className="info-panel-badge-row">
                                            {structuredInfo.tags.map((tag, index) => (
                                                <InfoBadge key={`${item._id || item.created_time}_structured_${index}`} tone={tag.tone}>
                                                    {tag.label}: {tag.value}
                                                </InfoBadge>
                                            ))}
                                        </div>
                                    ) : null}

                                    {item.summary_text ? <p className="info-summary-card__body">{item.summary_text}</p> : null}

                                    {structuredInfo.notes ? (
                                        <div className="info-panel-quote info-panel-quote--compact">
                                            <span className="info-panel-quote__label">Ghi chú từ structured data</span>
                                            <p>{structuredInfo.notes}</p>
                                        </div>
                                    ) : null}

                                    <div className="info-summary-card__footer">AI: {item.sync_status || 'unknown'}</div>
                                </article>
                            );
                        })}
                    </div>
                ) : (
                    <InfoEmpty>Chưa có lịch sử tóm tắt cuộc gọi.</InfoEmpty>
                )}
            </InfoSection>
        );
    }

    renderLeadSection = () => {
        const { contactInfoSelected, isSalesContact, handleEditInfoContact } = this.props;
        const from = contactInfoSelected.from || {};
        const aiInfo = contactInfoSelected.ai_info || {};
        const items = [
            { label: 'Họ tên', value: from.full_name_other || contactInfoSelected.thread_title || 'Chưa có' },
            { label: 'Email', value: from.email || 'Chưa có' },
            { label: 'Số điện thoại', value: from.phone || from.phone2 || 'Chưa có' },
            { label: 'Địa chỉ', value: from.address || 'Chưa có' }
        ];

        if (aiInfo.extracted_info && aiInfo.extracted_info.notes) {
            items.push({ label: 'Ghi chú AI', value: aiInfo.extracted_info.notes });
        }

        return (
            <InfoSection
                title="Thông tin lead"
                tone="light"
                actions={isSalesContact ? (

                    <Button
                            className="customer-order-list__refresh"
                            onClick={() => handleEditInfoContact(contactInfoSelected)}
                        >
                            <i className="fas fa-pencil-alt" />
                        </Button>
                ) : null}
            >
                <InfoMetaList items={items} />
            </InfoSection>
        );
    }

    renderPhoneSection = () => {
        const { contactInfoSelected, handleCallCustomer } = this.props;
        const phones = Array.isArray(contactInfoSelected.phone_list) ? contactInfoSelected.phone_list : [];

        return (
            <InfoSection title="Số điện thoại" tone="light">
                {phones.length > 0 ? (
                    <div className="info-phone-list">
                        {phones.map((phone, index) => (
                            <button
                                type="button"
                                className="info-phone-chip"
                                key={`${phone}_${index}`}
                                onClick={() => {
                                    if (handleCallCustomer) {
                                        handleCallCustomer({
                                            cus_phone: phone,
                                            contact_id: contactInfoSelected.contact_id
                                        });
                                    }
                                }}
                            >
                                <i className="material-icons">contact_phone</i>
                                <span>{phone}</span>
                            </button>
                        ))}
                    </div>
                ) : (
                    <InfoEmpty>Số điện thoại trong hội thoại sẽ xuất hiện tại đây.</InfoEmpty>
                )}
            </InfoSection>
        );
    }

    renderOrdersSection = () => {
        const { customerOrders, loadingCustomerOrders, confirmingOrderId } = this.state;
        const { onOpenOrderDetail } = this.props;

        return (
            <InfoSection
                title="Danh sách đơn hàng"
                tone="muted"
                style={{
                    padding: 0
                }}
                actions={(
                    <Tooltip title="Làm mới danh sách đơn hàng">
                        <Button
                            className="customer-order-list__refresh"
                            onClick={this.fetchCustomerOrders}
                            loading={loadingCustomerOrders}
                        >
                            <i className="fas fa-sync-alt" />
                        </Button>
                    </Tooltip>
                )}
            >
                {loadingCustomerOrders ? (
                    <InfoEmpty>Đang tải danh sách đơn hàng...</InfoEmpty>
                ) : customerOrders.length > 0 ? (
                    <div className="info-order-list">
                        {customerOrders.map((order) => {
                            const statusMeta = this.getOrderStatusMeta(order.status);
                            const isDraft = order.status === 'draft';
                            const isConfirming = confirmingOrderId === order._id;
                            const pricing = order.pricing || {};
                            const preview = this.getOrderPreviewItems(order);

                            return (
                                <article className="info-order-card" key={order._id || order.order_code} style={{ '--order-tone': statusMeta.color }}>
                                    <div className="info-order-card__header">
                                        <div className="info-order-card__headline">
                                            <div className="info-order-card__code">
                                                <i className="fas fa-receipt" />
                                                <span style={{ fontSize: 14}}>{order.order_code || 'Chưa có mã đơn'}</span>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="info-order-card__toggle"
                                            onClick={() => {
                                                if (onOpenOrderDetail) onOpenOrderDetail(order);
                                            }}
                                            title="Chi tiết đơn"
                                        >
                                            <i className="far fa-file-alt" />
                                        </button>
                                    </div>

                                    <div className="info-order-card__meta-row">
                                        <div className="info-order-card__meta-item">
                                            <span className="info-order-card__meta-label">Thời gian tạo</span>
                                            <strong className="info-order-card__meta-value">{moment(order.created_time).format('HH:mm DD/MM/YYYY')}</strong>
                                        </div>
                                        <div className="info-order-card__meta-item info-order-card__meta-item--amount">
                                            <span className="info-order-card__meta-label">Tổng tiền</span>
                                            <strong className="info-order-card__meta-value" style={{ color: statusMeta.color }}>
                                                {this.formatCurrency(pricing.grand_total || 0)}
                                            </strong>
                                        </div>
                                    </div>

                                    {preview.total > 0 ? (
                                        <div className="info-order-card__preview-row">
                                            <div className="info-order-card__preview-stack">
                                                {preview.items.map((item) => (
                                                    <span className="info-order-card__preview-thumb" key={item.key} title={item.label}>
                                                        {item.image ? (
                                                            <img
                                                                src={item.image}
                                                                alt={item.label}
                                                                onError={(event) => {
                                                                    event.target.src = orderPreviewFallback;
                                                                }}
                                                            />
                                                        ) : (
                                                            <i className="far fa-image" />
                                                        )}
                                                    </span>
                                                ))}
                                            </div>
                                            <span className="info-order-card__preview-count">
                                                {preview.total} sản phẩm
                                            </span>
                                        </div>
                                    ) : null}

                                    <div className="info-order-card__status-row">
                                        <span
                                            className="info-order-card__status-badge"
                                            style={{ color: statusMeta.color, borderColor: statusMeta.color, backgroundColor: `${statusMeta.color}12` }}
                                        >
                                            <i className={statusMeta.icon} /> {statusMeta.label}
                                        </span>
                                        {isDraft ? (
                                        <div className="info-order-card__actions">
                                            <Button type="primary" size="small" loading={isConfirming} onClick={() => this.handleConfirmDraftOrder(order)}>
                                                Xác nhận đơn
                                            </Button>
                                        </div>
                                        ) : null}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                ) : (
                    <InfoEmpty>Khách hàng chưa có đơn hàng nào.</InfoEmpty>
                )}
            </InfoSection>
        );
    }

    renderAppointmentsSection = () => {
        const bookingData = this.props.contactInfoSelected.booking && Array.isArray(this.props.contactInfoSelected.booking.data)
            ? this.props.contactInfoSelected.booking.data
            : [];

        return (
            <InfoSection title="Lịch hẹn" tone="light">
                {bookingData.length > 0 ? (
                    <div className="info-appointment-list">
                        {bookingData.map((item) => (
                            <div className="info-appointment-card" key={item._id}>
                                <div className="info-order-card__subheading">Code: {item.booking_code} ({item.address_code})</div>
                                <p>DV: {item.dich_vu && item.dich_vu.name}</p>
                                <p>
                                    TG: {item.booking_time}{' '}
                                    {item.status == 2 ? <span className="info-text-success">Thành công</span> : (item.status == 0 && item.booking_timestamp >= Date.now() ? <span className="info-text-accent">Đang chờ</span> : <span className="info-text-danger">Thất bại</span>)}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <InfoEmpty>Khách hàng chưa có lịch hẹn mới nào.</InfoEmpty>
                )}
            </InfoSection>
        );
    }

    renderCareNotesSection = () => {
        const { contactInfoSelected, showFormContactNote, isSalesContact, handleFormContactNote, saveContactNote, loadingBtnFormNote, listNoteContact, removeContactNote } = this.props;
        const notes = Array.isArray(listNoteContact) ? listNoteContact : [];

        return (
            <InfoSection
                title="Lịch chăm sóc"
                tone="light"
                actions={!showFormContactNote && isSalesContact ? (

                    <Button
                            className="customer-order-list__refresh"
                            onClick={handleFormContactNote}
                        >
                            <i className="fas fa-plus" />
                        </Button>
                ) : null}
            >
                {showFormContactNote ? (
                    <Form
                        onFinish={(values) => {
                            if (saveContactNote) saveContactNote(values);
                        }}
                        initialValues={{
                            contact_id: contactInfoSelected.contact_id,
                            notify_time: moment().add(1, 'day').startOf('day').unix() * 1000,
                            note_type: this.state.time_support ? 1 : 0
                        }}
                        className="info-care-form"
                        ref={(event) => { this.formContactNote = event; }}
                    >
                        <Form.Item name="contact_id" noStyle>
                            <Input type="hidden" />
                        </Form.Item>
                        <Form.Item
                            name="content"
                            rules={[{ required: true, message: 'Nội dung không được để trống' }]}
                        >
                            <Input.TextArea placeholder="Nhập nội dung cần chăm sóc..." />
                        </Form.Item>
                        <div className="info-care-form__controls">
                            <Form.Item noStyle name="note_type">
                                <Input type="hidden" />
                            </Form.Item>
                            <Form.Item noStyle name="notify_time">
                                <Input type="hidden" />
                            </Form.Item>
                            <Form.Item valuePropName="checked">
                                <Checkbox
                                    checked={this.state.time_support}
                                    onChange={(event) => this.setState({ time_support: event.target.checked }, () => {
                                        if (this.formContactNote) {
                                            this.formContactNote.setFieldsValue({
                                                note_type: event.target.checked ? 1 : 0
                                            });
                                        }
                                    })}
                                >
                                    Nhắc nhở
                                </Checkbox>
                            </Form.Item>
                            {this.state.time_support ? (
                                <Form.Item>
                                    <DatePicker
                                        format="HH:mm DD/MM/YYYY"
                                        size="small"
                                        showTime
                                        inputReadOnly
                                        defaultValue={moment().add(1, 'day').startOf('day')}
                                        disabledDate={(current) => current && current < moment().startOf('day')}
                                        onChange={(value) => {
                                            if (this.formContactNote) {
                                                this.formContactNote.setFieldsValue({
                                                    notify_time: value && value.startOf('hour').unix() * 1000
                                                });
                                            }
                                        }}
                                    />
                                </Form.Item>
                            ) : null}
                        </div>
                        <div className="info-care-form__actions">
                            <Button htmlType="button" type="default" onClick={handleFormContactNote} disabled={loadingBtnFormNote}>Hủy</Button>
                            <Button htmlType="submit" type="primary" loading={loadingBtnFormNote}>Lưu</Button>
                        </div>
                    </Form>
                ) : null}

                {!showFormContactNote && notes.length === 0 ? (
                    <InfoEmpty>Theo dõi các tương tác quan trọng của khách hàng. Chỉ những người quản lý Trang mới nhìn thấy hoạt động.</InfoEmpty>
                ) : null}

                {notes.length > 0 ? (
                    <div className="info-note-list" ref={(event) => { this.listNoteRef = event; }}>
                        {notes.map((item, index) => (
                            <article className="item-note" key={item._id || `note_${index}`}>
                                <div className="avatar">
                                    <img src={require('../img/user.jpg')} alt={item.from && item.from.fullname ? item.from.fullname : 'avatar'} />
                                </div>
                                <div className="content-note">
                                    <div className="head">
                                        <h3 className="user-name">{item.from ? item.from.fullname : 'Nội bộ'}</h3>
                                        <span>
                                            {item.status == 1 && isSalesContact ? (
                                                <svg
                                                    onClick={() => {
                                                        if (removeContactNote) removeContactNote(item._id);
                                                    }}
                                                    fill="currentColor"
                                                    preserveAspectRatio="xMidYMid meet"
                                                    height="1em"
                                                    width="1em"
                                                    viewBox="0 0 40 40"
                                                    className="delete-note-btn"
                                                >
                                                    <g>
                                                        <path d="m28.6 25q0-0.5-0.4-1l-4-4 4-4q0.4-0.5 0.4-1 0-0.6-0.4-1.1l-2-2q-0.4-0.4-1-0.4-0.6 0-1 0.4l-4.1 4.1-4-4.1q-0.4-0.4-1-0.4-0.6 0-1 0.4l-2 2q-0.5 0.5-0.5 1.1 0 0.5 0.5 1l4 4-4 4q-0.5 0.5-0.5 1 0 0.7 0.5 1.1l2 2q0.4 0.4 1 0.4 0.6 0 1-0.4l4-4.1 4.1 4.1q0.4 0.4 1 0.4 0.6 0 1-0.4l2-2q0.4-0.4 0.4-1z m8.7-5q0 4.7-2.3 8.6t-6.3 6.2-8.6 2.3-8.6-2.3-6.2-6.2-2.3-8.6 2.3-8.6 6.2-6.2 8.6-2.3 8.6 2.3 6.3 6.2 2.3 8.6z" />
                                                    </g>
                                                </svg>
                                            ) : null}
                                            {(Date.now() - item.updated_time) >= (60 * 1000 * 60 * 24)
                                                ? `${moment(item.updated_time).format('HH')}h ${moment(item.updated_time).format('DD-MM-YYYY')}`
                                                : moment(item.updated_time).format('HH:mm')}
                                        </span>
                                    </div>
                                    <p>{item.content}</p>
                                    {item.note_type == 1 ? (
                                        <div className="notify-time">
                                            <p className="text-right">
                                                {moment(item.notify_time).format('HH:mm DD/MM/YYYY')} <i className="fa fa-clock ml-2 text-danger" />
                                            </p>
                                        </div>
                                    ) : null}
                                </div>
                            </article>
                        ))}
                    </div>
                ) : null}
            </InfoSection>
        );
    }

    renderTagsSection = () => {
        const { contactInfoSelected, isSalesContact, handleUpdateTag, handleFavouriteTag, handleSelectTagChat } = this.props;
        const tagGroups = buildTagGroups(this.props.settingSystem);
        const selectedTagId = getSelectedTagId(tagGroups);
        const selectedGroup = tagGroups.find((item) => item.tag_id == selectedTagId);
        const tags = Array.isArray(contactInfoSelected.tags) ? contactInfoSelected.tags : [];

        return (
            <InfoSection title="Nhãn" tone="light">
                <div className="list-tags">
                    {tags.length > 0 ? (
                        <div className="info-panel-badge-row">
                            {tags.map((item) => (
                                <Tag
                                    key={item.tag_id}
                                    color={`${item.tag_color}`}
                                    className="info-tag-selected"
                                    closable={isSalesContact ? true : false}
                                    onClose={() => {
                                        if (handleUpdateTag) {
                                            handleUpdateTag({
                                                tag: item,
                                                contact: contactInfoSelected
                                            });
                                        }
                                    }}
                                >
                                    {item.tag_name}
                                </Tag>
                            ))}
                        </div>
                    ) : (
                        <InfoEmpty>Khách hàng chưa được gắn nhãn.</InfoEmpty>
                    )}

                    {tagGroups.length > 0 ? (
                        <div className="info-tag-manager">
                            <div className="info-tag-manager__select">
                                <span className="info-panel-footnote">Nhóm nhãn</span>
                                <Select value={selectedTagId} size="small" onChange={handleSelectTagChat}>
                                    {tagGroups.map((item) => (
                                        <Option value={item.tag_id} key={item.tag_id}>{item.tag_name}</Option>
                                    ))}
                                </Select>
                            </div>

                            <table className="table-bordered table-vcenter text-center table-tags info-tag-table">
                                <tbody className={`${!isSalesContact ? 'disable-sale' : ''}`}>
                                    <tr>
                                        <td className="text-left">Tên</td>
                                        <td>Màu</td>
                                        <td>Hành động</td>
                                    </tr>
                                    {selectedGroup ? selectedGroup.tag_childs.map((item) => (
                                        <tr key={item.tag_id}>
                                            <td className="text-left info-tag-table__name">
                                                <span
                                                    onClick={() => {
                                                        if (handleUpdateTag) {
                                                            handleUpdateTag({
                                                                tag: item,
                                                                contact: contactInfoSelected
                                                            });
                                                        }
                                                    }}
                                                >
                                                    {item.tag_name}
                                                </span>
                                            </td>
                                            <td style={{ padding: '5px' }}>
                                                <Tag key={item.tag_id} color={`${item.tag_color}`} style={{ marginBottom: '0px' }} />
                                            </td>
                                            <td>
                                                <i
                                                    style={{ cursor: 'pointer' }}
                                                    className={tags.find((tag) => tag.tag_id == item.tag_id) ? 'fa fa-tags' : 'fal fa-tags'}
                                                    onClick={() => {
                                                        if (handleUpdateTag) {
                                                            handleUpdateTag({
                                                                tag: item,
                                                                contact: contactInfoSelected
                                                            });
                                                        }
                                                    }}
                                                />
                                                <Tooltip title="Nhãn yêu thích">
                                                    <i
                                                        style={{ cursor: 'pointer' }}
                                                        className={item.is_favourite ? 'fa fa-heart ml-2 text-danger' : 'fal fa-heart ml-2'}
                                                        onClick={() => {
                                                            if (handleFavouriteTag) {
                                                                handleFavouriteTag({
                                                                    tag: item,
                                                                    is_favourite: item.is_favourite ? 0 : 1
                                                                });
                                                            }
                                                        }}
                                                    />
                                                </Tooltip>
                                            </td>
                                        </tr>
                                    )) : null}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <InfoEmpty>Công ty bạn chưa có nhãn nào, vui lòng liên hệ quản lý công ty để thêm nhãn mới.</InfoEmpty>
                    )}
                </div>
            </InfoSection>
        );
    }

    render() {
        const { contactInfoSelected } = this.props;
        if (!contactInfoSelected) return null;

        return (
            <div id="info-view-content" className="scrollable-panel info-panel-content">
                <div className="info-panel-stack">
                    <InfoHero contact={contactInfoSelected} aiInfo={contactInfoSelected.ai_info} />
                    {this.renderAiSection(contactInfoSelected.ai_info)}
                    {this.renderMemorySection()}
                    {this.renderCallSummariesSection()}
                    
                        {this.renderLeadSection()}
                        {this.renderPhoneSection()}
                    {this.renderOrdersSection()}
                    
                        {this.renderAppointmentsSection()}
                        {this.renderCareNotesSection()}
                    {this.renderTagsSection()}
                </div>
            </div>
        );
    }
}
